export interface BestFlight {
  flight_id: number;
  value: number;
}

export interface BestFlightDuration {
  flight_id: number;
  minutes: number;
}

export interface MonthStats {
  year: number;
  month: number;
  flight_count: number;
  total_minutes: number;
  total_xc_distance: number;
}

export interface CountryFlightStats {
  code: string;
  flight_count: number;
}

export interface PilotStats {
  flight_count: number;
  total_minutes: number;
  total_xc_distance: number;
  takeoffs_visited: number;
  countries_visited: number;
  countries: CountryFlightStats[];
  best_distance?: BestFlight;
  longest_flight?: BestFlightDuration;
  max_altitude?: BestFlight;
  best_scores?: Record<string, number>;
  monthly_stats?: MonthStats[];
}

export interface PilotPayload {
  id: number;
  is_admin: boolean;
  nick: string;
  name: string;
  sub: string;
  email?: string;
  avatar: string;
  post: string;
  profile_picture_id?: string | null;
  syride_pilot?: string | null;
  syride_login?: string | null;
  xcontest_login?: string | null;
  stats?: PilotStats | null;
}

export interface AboutMePayload {
  authenticated: boolean;
  // Add other fields as needed
  pilot?: PilotPayload;
}



export interface UpTrackStats {
  distance_m: number
  duration_ns: number
  elevation_gain_m: number
}

export interface Statistics {
  duration: string;
  distance: number;
  compounded_distance: number;
  average_speed_ms: number;
  average_speed_kmh: number;
  max_speed_ms: number;
  max_speed_kmh: number;
  top_altitude: number;
  max_gain: number;
  max_climb_rate: number;
  scores?: Record<string, number>;
  up_track?: UpTrackStats;
}

export interface Coordinates {
  lat: number;
  lon: number;
}
export interface Position {
  coordinates: Coordinates;
  timestamp: Date;
  altitude: number;
  ground_altitude: number;
  agl: number;
  speed: number;
  gain: number;
  climb_rate: number;
  distance: number;
}

export type Exposition = 'N' | 'NE' | 'E' | 'SE' | 'S' | 'SW' | 'W' | 'NW';

export interface TakeOffPayload {
  id: number;
  name: string;
  coordinates: string;
  description?: string;
  expositions: Exposition[];
  altitude: number;
  country: string;
}

export interface GliderStatsPayload {
  flight_count: number;
  total_minutes: number;
  flights_since_control: number;
  airtime_since_control: number;
}

export interface GliderPayload {
  id: string;
  pilot_nick: string;
  name: string;
  brand: string;
  model: string;
  category?: string;
  size: string;
  color: string;
  retired?: boolean;
  manufactured_at?: string;
  last_control_at?: string;
  stats?: GliderStatsPayload;
}

export interface CommentPayload {
  id: number;
  flight_id: number;
  pilot_id: number;
  pilot_nick: string;
  pilot_name: string;
  pilot_avatar: string;
  body: string;
  created_at: string;
}

export interface MediaPayload {
  id: string;
  pilot_id: number;
  flight_id?: number;
  takeoff_id?: number;
  adventure_id?: number;
  filename: string;
  content_type: string;
  size: number;
  storage_key: string;
  created_at: string;
}

export interface AdventurePayload {
  id: number;
  title: string;
  description?: string;
  pilot_id: number;
  created_at: string;
}

export interface AdventureCommentPayload {
  id: number;
  adventure_id: number;
  pilot_id: number;
  pilot_nick: string;
  pilot_name: string;
  pilot_avatar: string;
  body: string;
  created_at: string;
}

export interface AdventureDetailPayload {
  adventure: AdventurePayload;
  flights: FlightPayload[];
  comments: AdventureCommentPayload[];
  photos: MediaPayload[];
}

export interface FlightPayload {
  id: number;
  pilot_id: number;
  pilot_nick?: string;
  pilot_name?: string;
  pilot_avatar?: string;
  pilot_profile_picture_id?: string | null;
  post?: string | null;
  title?: string;
  type: string;
  takeoff_id?: number;
  takeoff_name: string;
  takeoff_coords: string;
  takeoff_country?: string;
  start_time: Date;
  end_time: Date;
  glider_id?: string;
  glider_name?: string;
  igc_track: string;
  json_track: Position[];
  gpx_up_track?: string;
  json_up_track?: Position[];
  statistics: Statistics;
  metadata: Record<string, any>;
  comment_count: number;
  photo_count: number;
  adventure_id?: number;
  igc_url?: string;
  gpx_url?: string;
  prev_flight_id?: number;
  next_flight_id?: number;
}

export interface AdminJobFailure {
  at: string;
  attempt: number;
  message: string;
}

export interface AdminJobRun {
  id: number;
  kind: string;
  state: string;
  attempt: number;
  max_attempts: number;
  args: string;
  created_at: string;
  scheduled_at: string;
  attempted_at?: string;
  finalized_at?: string;
  cancel_requested_at?: string;
  latest_error?: AdminJobFailure;
}

export interface AdminEventSubject {
  type: string;
  id: string;
}

export interface AdminEvent {
  id: number;
  kind: string;
  message: string;
  retention: 'ephemeral' | 'persistent';
  subjects: AdminEventSubject[];
  data: Record<string, unknown>;
  created_at: string;
}

/** Convert an ISO 3166-1 alpha-2 country code to a flag emoji. Returns '' for empty/invalid codes. */
export function countryFlag(code: string): string {
  if (!code || code.length !== 2) return ''
  const cp = [...code.toUpperCase()].map(c => 0x1f1e6 + c.charCodeAt(0) - 65)
  return String.fromCodePoint(...cp)
}
